import React, { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { FaRegCalendarAlt, FaChevronDown } from "react-icons/fa";

const normalizeDateValue = (value) => {
  if (!value) return "";
  const raw = typeof value === "string" ? value.split("T")[0] : value;
  const parts = typeof raw === "string" ? raw.split("-").map(Number) : null;
  let date = null;
  if (parts && parts.length === 3 && parts.every((n) => Number.isFinite(n))) {
    date = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2], 12));
  } else {
    date = new Date(value);
  }
  if (Number.isNaN(date.getTime())) return "";
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(date.getUTCDate()).padStart(2, "0");
  const yyyy = date.getUTCFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

const InfoSection = ({ data, onChange, showStatusBlink }) => {
  const [openDropdownKey, setOpenDropdownKey] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const [openDateKey, setOpenDateKey] = useState(null);
  const [datePickerPos, setDatePickerPos] = useState({ top: 0, left: 0, width: 0 });
  const buttonRefs = useRef({});
  const dropdownRef = useRef(null);
  const datePickerRef = useRef(null);
  
  const handleDropdownToggle = (key) => {
    const rect = buttonRefs.current[key]?.getBoundingClientRect();
    if (rect) {
      setDropdownPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX, width: rect.width });
    }
    setOpenDropdownKey(prev => (prev === key ? null : key));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdownKey(null);
      }
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setOpenDateKey(null);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpenDropdownKey(null);
        setOpenDateKey(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
  <>
    <div className="flex gap-fluid-2 items-center w-full overflow-x-auto flex-nowrap scrollbar-hide">

      {Object.entries(data)
        .filter(([_, field]) => field)        // ⭐ prevents crash from null
        .map(([key, field]) => (
          <div
            key={key}
            className="relative flex flex-col min-w-[clamp(8rem,12vw,10rem)] flex-1"
          >
            {/* LABEL */}
            <span className="text-fluid-sm font-semibold text-gray-700 dark:text-acs-dark-text whitespace-nowrap flex items-center gap-1">
              {field?.label || key}

              {/* ACTIVE STATUS BLINK */}
              {key === "Status" && field?.value === "Active" && (
                <span className="relative flex h-1 w-1 ml-1">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-[#223688] dark:bg-[#31C4AB] opacity-50 animate-radiate"></span>
                  <span className="relative inline-flex rounded-full h-1 w-1 bg-[#223688] dark:bg-[#31C4AB]"></span>
                </span>
              )}
            </span>

            {/* FIELD INPUT */}
            <div className="relative w-full">
              {/* DROPDOWN */}
              {field?.type === "dropdown" ? (
                <>
                  <button
                    type="button"
                    onClick={() => handleDropdownToggle(key)}
                    ref={(el) => (buttonRefs.current[key] = el)}
                    className="w-full text-fluid-sm px-2 py-1 
                               bg-[#FAFAFB] dark:bg-acs-dark-surface
                               border border-gray-300 dark:border-acs-dark-border 
                               rounded-md flex items-center justify-between 
                               transition-none"
                  >
                    <span
                      className={
                        field?.isPlaceholder
                          ? "text-gray-400 dark:text-gray-500"
                          : "text-gray-800 dark:text-acs-dark-text"
                      }
                    >
                      {field?.value || "Select..."}
                    </span>

                    <FaChevronDown
                      className={`w-fluid-icon-xs h-fluid-icon-xs text-[#223688] dark:text-[#31C4AB] transition-transform duration-300 ${
                        openDropdownKey === key ? "rotate-180" : "rotate-0"
                      }`}
                    />
                  </button>
                </>
              ) : field?.type === "date" ? (
                // DATE FIELD WITH DAYPICKER
                <div className="relative flex items-center">
                  <button
                    type="button"
                    onClick={() => {
                      if (field?.readonly) return;
                      const rect = buttonRefs.current[key]?.getBoundingClientRect();
                      if (rect) {
                        setDatePickerPos({
                          top: rect.bottom + window.scrollY,
                          left: rect.left + window.scrollX,
                          width: rect.width,
                        });
                      }
                      setOpenDropdownKey(null);
                      setOpenDateKey((prev) => (prev === key ? null : key));
                    }}
                    ref={(el) => (buttonRefs.current[key] = el)}
                    className="w-full px-fluid-2 py-fluid-1 rounded-md text-fluid-sm 
                               bg-[#FAFAFB] dark:bg-acs-dark-surface
                               text-gray-800 dark:text-acs-dark-text 
                               border border-gray-300 dark:border-acs-dark-border 
                               appearance-none focus:outline-none focus:ring-0
                               flex items-center justify-between"
                    disabled={field?.readonly}
                  >
                    <span className={field?.value ? "" : "text-gray-400 dark:text-gray-500"}>
                      {normalizeDateValue(field?.value) || "Select date"}
                    </span>
                    <FaRegCalendarAlt className="w-fluid-icon-xs h-fluid-icon-xs text-[#223688] dark:text-[#31C4AB]" />
                  </button>

                  {openDateKey === key && !field?.readonly &&
                    createPortal(
                      <div
                        ref={datePickerRef}
                        style={{
                          position: "absolute",
                          top: datePickerPos.top + 6,
                          left: datePickerPos.left,
                          zIndex: 9999,
                        }}
                        className="bg-white dark:bg-acs-dark-bg border border-gray-200 dark:border-acs-dark-border rounded-lg shadow-lg p-fluid-2"
                      >
                        <DayPicker
                          mode="single"
                          selected={field?.value ? new Date(normalizeDateValue(field.value)) : undefined}
                          onSelect={(day) => {
                            const formatted = day
                              ? `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`
                              : "";
                            onChange(key, formatted);
                            setOpenDateKey(null);
                          }}
                          weekStartsOn={1}
                        />
                      </div>,
                      document.body
                    )}
                </div>
              ) : (
                // TEXT FIELD
                <input
                  type="text"
                  value={field?.value}
                  onChange={(e) => onChange(key, e.target.value)}
                  readOnly={field?.readonly}
                  className="w-full px-fluid-2 py-fluid-1 rounded-md text-fluid-sm 
                             bg-[#FAFAFB] dark:bg-acs-dark-surface
                             text-gray-800 dark:text-acs-dark-text 
                             border border-gray-300 dark:border-acs-dark-border 
                             appearance-none focus:outline-none focus:ring-0"
                />
              )}
            </div>
          </div>
        ))}
    </div>

    {/* PORTAL DROPDOWN */}
    {openDropdownKey && data[openDropdownKey]?.options &&
      createPortal(
        <>
          <style>{`
            @keyframes ha-in-right {
              from { opacity: 0; transform: translateX(10px); }
              to { opacity: 1; transform: translateX(0); }
            }
            .ha-dropdown { animation: ha-in-right 200ms ease-out both; }
          `}</style>

          <div
            ref={dropdownRef}
            data-ha-menu="true"
            style={{
              position: "absolute",
              top: dropdownPos.top + 5,
              left: dropdownPos.left,
              width: dropdownPos.width,
              zIndex: 9999,
            }}
            className="ha-dropdown fixed z-[9999] 
                       border border-gray-200 dark:border-acs-dark-border 
                       rounded-md shadow-lg 
                       bg-white dark:bg-acs-dark-bg"
          >
            <div className="flex flex-col p-fluid-2">
              {data[openDropdownKey]?.options?.map((option, idx) => (
                <button
                  key={idx}
                  className="flex items-center gap-fluid-1 
                             w-full text-left 
                             px-fluid-3 py-fluid-1.5 
                             text-gray-600 dark:text-acs-dark-text 
                             text-fluid-xs sm:text-fluid-sm 
                             hover:bg-gray-50 dark:hover:bg-[#1f222a] 
                             transition-colors rounded-md"
                  onClick={() => {
                    onChange(openDropdownKey, option);
                    setOpenDropdownKey(null);
                  }}
                >
                  <span className="whitespace-nowrap">{option}</span>
                </button>
              ))}
            </div>
          </div>
        </>,
        document.body
      )}
  </>
);

};

export default InfoSection;
